/**
 * Admin management service for Firebase
 * Handles admin dashboard, analytics, user management, and content moderation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  increment,
  arrayUnion,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from './config';
import {
  DashboardAnalytics,
  UserAnalytics,
  AdminUserProfile,
  ModerationAction,
  SystemSettings,
  AuditLog,
  AnalyticsTimePeriod,
  UserStatus,
  ContentStatus,
  AdminRole,
} from '../../types/admin';

/**
 * Admin service class
 */
export class AdminService {
  private static instance: AdminService;
  
  public static getInstance(): AdminService {
    if (!AdminService.instance) {
      AdminService.instance = new AdminService();
    }
    return AdminService.instance;
  }

  /**
   * Get pending affiliate applications
   */
  public async getPendingAffiliateApplications(): Promise<any[]> {
    try {
      const q = query(
        collection(db, 'affiliate_applications'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching affiliate applications:', error);
      throw error;
    }
  }

  /**
   * Get all affiliate applications
   */
  public async getAffiliateApplications(status?: string): Promise<any[]> {
    try {
      let q;
      if (status) {
        q = query(
          collection(db, 'affiliate_applications'),
          where('status', '==', status),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          collection(db, 'affiliate_applications'),
          orderBy('createdAt', 'desc')
        );
      }
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching affiliate applications:', error);
      throw error;
    }
  }

  /**
   * Get active affiliates
   */
  public async getAffiliates(): Promise<any[]> {
    try {
      const q = query(
        collection(db, 'affiliates'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      throw error;
    }
  }

  /**
   * Approve affiliate application
   */
  public async approveAffiliate(applicationId: string): Promise<void> {
    try {
      const approveFn = httpsCallable(functions, 'approveAffiliateApplication');
      await approveFn({ applicationId });
    } catch (error) {
      console.error('Error approving affiliate:', error);
      throw error;
    }
  }

  /**
   * Reject affiliate application
   */
  public async rejectAffiliate(applicationId: string, reason: string): Promise<void> {
    try {
      const rejectFn = httpsCallable(functions, 'rejectAffiliateApplication');
      await rejectFn({ applicationId, reason });
    } catch (error) {
      console.error('Error rejecting affiliate:', error);
      throw error;
    }
  }

  /**
   * Check if current user has admin access

   */
  private async checkAdminAccess(): Promise<boolean> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        return false;
      }

      const userData = userDoc.data();
      const userRoles = userData.roles || [];
      const hasAdminRole = userRoles.some((role: string) => 
        Object.values(AdminRole).includes(role as AdminRole)
      );
      return hasAdminRole;
    } catch (error) {
      return false;
    }
  }

  // ==================== DASHBOARD ANALYTICS ====================

  /**
   * Get dashboard analytics
   */
  // Monthly price mapping for MRR calculation (in cents)
  private readonly TIER_MONTHLY_PRICES: Record<string, number> = {
    free: 0,
    standard: 2900, // $29/month
    premium: 4900,  // $49/month
    annual: 2400,   // $24/month (billed annually at $288)
    twelve_month_package: 1900, // $19/month equivalent
    corporate: 9900, // $99/month
    stackcommerce_lifetime: 0, // One-time, no MRR
  };

  async getDashboardAnalytics(period: AnalyticsTimePeriod = AnalyticsTimePeriod.LAST_30_DAYS): Promise<DashboardAnalytics> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      const { startDate, endDate } = this.getDateRange(period);

      // Get user metrics
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnapshot.size;

      const activeUsersQuery = query(
        collection(db, 'users'),
        where('lastActiveAt', '>=', Timestamp.fromDate(startDate))
      );
      const activeUsersSnapshot = await getDocs(activeUsersQuery);
      const activeUsers = activeUsersSnapshot.size;

      const newUsersQuery = query(
        collection(db, 'users'),
        where('createdAt', '>=', Timestamp.fromDate(startDate))
      );
      const newUsersSnapshot = await getDocs(newUsersQuery);
      const newUsers = newUsersSnapshot.size;

      // Get Daily Active Users (users active today)
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      const dauQuery = query(
        collection(db, 'users'),
        where('lastActiveAt', '>=', Timestamp.fromDate(todayMidnight))
      );
      const dauSnapshot = await getDocs(dauQuery);
      const dailyActiveUsers = dauSnapshot.size;

      // Get course metrics
      const coursesSnapshot = await getDocs(collection(db, 'courses'));
      const totalCourses = coursesSnapshot.size;

      const publishedCoursesQuery = query(
        collection(db, 'courses'),
        where('isPublished', '==', true)
      );
      const publishedCoursesSnapshot = await getDocs(publishedCoursesQuery);
      const publishedCourses = publishedCoursesSnapshot.size;

      // Get enrollment metrics
      const enrollmentsSnapshot = await getDocs(collection(db, 'user_progress'));
      const totalEnrollments = enrollmentsSnapshot.size;

      const completedEnrollmentsQuery = query(
        collection(db, 'user_progress'),
        where('status', '==', 'completed')
      );
      const completedEnrollmentsSnapshot = await getDocs(completedEnrollmentsQuery);
      const completedEnrollments = completedEnrollmentsSnapshot.size;

      // Get community metrics
      const postsSnapshot = await getDocs(collection(db, 'posts'));
      const totalPosts = postsSnapshot.size;

      const messagesSnapshot = await getDocs(collection(db, 'messages'));
      const totalMessages = messagesSnapshot.size;

      // Get revenue metrics from subscriptions
      const { totalRevenue, monthlyRecurringRevenue, payingUsers, revenueGrowthRate } =
        await this.calculateRevenueMetrics(startDate);

      // Get average session duration from learning_sessions
      const averageSessionDuration = await this.calculateAverageSessionDuration();

      // Get support ticket metrics
      const supportMetrics = await this.getSupportTicketAnalytics();

      // Calculate derived metrics
      const userGrowthRate = this.calculateGrowthRate(newUsers, totalUsers - newUsers);
      const completionRate = totalEnrollments > 0 ? (completedEnrollments / totalEnrollments) * 100 : 0;
      const averageRevenuePerUser = payingUsers > 0 ? totalRevenue / payingUsers : 0;

      return {
        // User metrics
        totalUsers,
        activeUsers,
        newUsers,
        userGrowthRate,

        // Course metrics
        totalCourses,
        publishedCourses,
        totalEnrollments,
        completionRate,

        // Revenue metrics (from real subscription data)
        totalRevenue,
        monthlyRecurringRevenue,
        averageRevenuePerUser,
        revenueGrowthRate,

        // Engagement metrics
        totalPosts,
        totalMessages,
        averageSessionDuration,
        dailyActiveUsers,

        // Support metrics (from real ticket data)
        totalTickets: supportMetrics.totalTickets,
        openTickets: supportMetrics.openTickets,
        averageResponseTime: supportMetrics.averageResponseTime,
        customerSatisfactionScore: supportMetrics.customerSatisfactionScore,

        // Time period
        period,
        startDate,
        endDate,
        lastUpdated: new Date(),
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Calculate revenue metrics from subscriptions collection
   */
  private async calculateRevenueMetrics(periodStartDate: Date): Promise<{
    totalRevenue: number;
    monthlyRecurringRevenue: number;
    payingUsers: number;
    revenueGrowthRate: number;
  }> {
    try {
      // Get all active subscriptions
      const activeSubsQuery = query(
        collection(db, 'subscriptions'),
        where('status', 'in', ['active', 'trialing'])
      );
      const activeSubsSnapshot = await getDocs(activeSubsQuery);

      let monthlyRecurringRevenue = 0;
      let payingUsers = 0;

      activeSubsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const tier = data.tier || 'free';
        const monthlyPrice = this.TIER_MONTHLY_PRICES[tier] || 0;

        if (monthlyPrice > 0) {
          monthlyRecurringRevenue += monthlyPrice;
          payingUsers++;
        }
      });

      // Convert from cents to dollars
      monthlyRecurringRevenue = monthlyRecurringRevenue / 100;

      // Calculate total revenue (estimate based on MRR * months in period)
      const now = new Date();
      const monthsInPeriod = Math.max(1, Math.ceil(
        (now.getTime() - periodStartDate.getTime()) / (30 * 24 * 60 * 60 * 1000)
      ));
      const totalRevenue = monthlyRecurringRevenue * monthsInPeriod;

      // Calculate growth rate by comparing current vs previous period subscriptions
      const previousPeriodStart = new Date(periodStartDate);
      previousPeriodStart.setMonth(previousPeriodStart.getMonth() - 1);

      const previousSubsQuery = query(
        collection(db, 'subscriptions'),
        where('status', 'in', ['active', 'trialing']),
        where('createdAt', '<', Timestamp.fromDate(periodStartDate)),
        where('createdAt', '>=', Timestamp.fromDate(previousPeriodStart))
      );

      let previousMRR = 0;
      try {
        const previousSubsSnapshot = await getDocs(previousSubsQuery);
        previousSubsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const tier = data.tier || 'free';
          const monthlyPrice = this.TIER_MONTHLY_PRICES[tier] || 0;
          previousMRR += monthlyPrice;
        });
        previousMRR = previousMRR / 100;
      } catch {
        // If query fails (e.g., missing index), just use 0
        previousMRR = 0;
      }

      const revenueGrowthRate = this.calculateGrowthRate(monthlyRecurringRevenue, previousMRR);

      return {
        totalRevenue,
        monthlyRecurringRevenue,
        payingUsers,
        revenueGrowthRate,
      };
    } catch (error) {
      console.error('Error calculating revenue metrics:', error);
      // Return zeros if we can't calculate revenue
      return {
        totalRevenue: 0,
        monthlyRecurringRevenue: 0,
        payingUsers: 0,
        revenueGrowthRate: 0,
      };
    }
  }

  /**
   * Calculate average session duration from learning_sessions collection
   */
  private async calculateAverageSessionDuration(): Promise<number> {
    try {
      // Query recent learning sessions (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const sessionsQuery = query(
        collection(db, 'learning_sessions'),
        where('isActive', '==', false), // Only completed sessions
        limit(1000) // Limit to prevent large reads
      );

      const sessionsSnapshot = await getDocs(sessionsQuery);

      if (sessionsSnapshot.empty) {
        return 0; // No session data
      }

      let totalDuration = 0;
      let sessionCount = 0;

      sessionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Calculate duration from startTime and endTime if available
        if (data.startTime && data.endTime) {
          const startTime = data.startTime.toDate ? data.startTime.toDate() : new Date(data.startTime);
          const endTime = data.endTime.toDate ? data.endTime.toDate() : new Date(data.endTime);
          const duration = (endTime.getTime() - startTime.getTime()) / 1000; // in seconds

          // Only count reasonable sessions (between 1 minute and 4 hours)
          if (duration > 60 && duration < 14400) {
            totalDuration += duration;
            sessionCount++;
          }
        } else if (data.duration) {
          // If duration is stored directly
          const duration = typeof data.duration === 'number' ? data.duration : 0;
          if (duration > 60 && duration < 14400) {
            totalDuration += duration;
            sessionCount++;
          }
        }
      });

      return sessionCount > 0 ? Math.round(totalDuration / sessionCount) : 0;
    } catch (error) {
      console.error('Error calculating average session duration:', error);
      return 0; // Return 0 if we can't calculate
    }
  }

  /**
   * Get user analytics
   */
  async getUserAnalytics(
    userId: string,
    period: AnalyticsTimePeriod = AnalyticsTimePeriod.LAST_30_DAYS
  ): Promise<UserAnalytics | null> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      
      // Get user data
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        return null;
      }
      
      const userData = userDoc.data();
      
      // Get user's course enrollments
      const enrollmentsQuery = query(
        collection(db, 'user_progress'),
        where('userId', '==', userId)
      );
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      const coursesEnrolled = enrollmentsSnapshot.size;
      
      const completedEnrollmentsQuery = query(
        collection(db, 'user_progress'),
        where('userId', '==', userId),
        where('status', '==', 'completed')
      );
      const completedEnrollmentsSnapshot = await getDocs(completedEnrollmentsQuery);
      const coursesCompleted = completedEnrollmentsSnapshot.size;
      
      // Get user's posts and replies
      const postsQuery = query(
        collection(db, 'posts'),
        where('authorId', '==', userId)
      );
      const postsSnapshot = await getDocs(postsQuery);
      const postsCreated = postsSnapshot.size;
      
      const repliesQuery = query(
        collection(db, 'replies'),
        where('authorId', '==', userId)
      );
      const repliesSnapshot = await getDocs(repliesQuery);
      const repliesCreated = repliesSnapshot.size;
      
      // Calculate metrics
      const totalLearningTime = enrollmentsSnapshot.docs.reduce((total, doc) => {
        return total + (doc.data().totalTimeSpent || 0);
      }, 0);
      
      const averageProgress = enrollmentsSnapshot.docs.reduce((total, doc) => {
        return total + (doc.data().completionPercentage || 0);
      }, 0) / (coursesEnrolled || 1);
      
      return {
        userId,
        
        // Activity metrics (mock data - would track in real implementation)
        totalSessions: 45,
        totalSessionDuration: 32400, // 9 hours
        averageSessionDuration: 720, // 12 minutes
        lastActiveAt: userData.lastActiveAt?.toDate() || new Date(),
        
        // Learning metrics
        coursesEnrolled,
        coursesCompleted,
        totalLearningTime,
        averageProgress,
        
        // Engagement metrics
        postsCreated,
        repliesCreated,
        messagesExchanged: 0, // Would calculate from messages collection
        likesReceived: 0, // Would calculate from likes on posts/replies
        
        // Subscription metrics
        subscriptionTier: userData.subscriptionTier || 'free',
        subscriptionStartDate: userData.subscriptionStartDate?.toDate(),
        totalSpent: 0, // Would integrate with Stripe
        
        // Geographic and device info (mock data)
        country: 'United States',
        city: 'San Francisco',
        deviceType: 'Desktop',
        browserType: 'Chrome',
        
        // Calculated metrics
        engagementScore: this.calculateEngagementScore(postsCreated, repliesCreated, coursesCompleted),
        retentionScore: 85,
        lifetimeValue: 150,
        
        // Time period
        period,
        calculatedAt: new Date(),
      };
    } catch (error) {
      throw error;
    }
  }

  // ==================== USER MANAGEMENT ====================

  /**
   * Get all users with filtering and pagination
   */
  async getUsers(filters?: {
    status?: UserStatus;
    role?: string;
    subscriptionTier?: string;
    searchTerm?: string;
    limit?: number;
    lastDoc?: any;
  }): Promise<{ users: AdminUserProfile[]; hasMore: boolean; lastDoc: any }> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      let q = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc')
      );

      // Apply filters
      if (filters?.status) {
        q = query(q, where('status', '==', filters.status));
      }
      
      if (filters?.subscriptionTier) {
        q = query(q, where('subscriptionTier', '==', filters.subscriptionTier));
      }

      const pageLimit = filters?.limit || 50;
      q = query(q, limit(pageLimit + 1));

      if (filters?.lastDoc) {
        q = query(q, startAfter(filters.lastDoc));
      }

      const snapshot = await getDocs(q);
      const users: AdminUserProfile[] = [];

      snapshot.docs.forEach((doc, index) => {
        if (index < pageLimit) {
          const data = doc.data();
          
          // Filter by search term if provided
          if (filters?.searchTerm) {
            const searchLower = filters.searchTerm.toLowerCase();
            const matchesSearch = 
              data.email?.toLowerCase().includes(searchLower) ||
              data.displayName?.toLowerCase().includes(searchLower) ||
              data.firstName?.toLowerCase().includes(searchLower) ||
              data.lastName?.toLowerCase().includes(searchLower);
            
            if (!matchesSearch) return;
          }
          
          users.push({
            id: doc.id,
            email: data.email || '',
            displayName: data.displayName || '',
            firstName: data.firstName,
            lastName: data.lastName,
            avatar: data.avatar,
            
            // Account status
            status: data.status || UserStatus.ACTIVE,
            emailVerified: data.emailVerified || false,
            phoneVerified: data.phoneVerified || false,
            
            // Roles and permissions
            roles: data.roles || [],
            permissions: data.permissions || [],
            isAffiliate: false, // Will be populated separately
            acquisitionSource: data.acquisitionSource,
            intendedDestination: data.intendedDestination,


            // Subscription info
            subscriptionTier: data.subscriptionTier || 'free',
            subscriptionStatus: data.subscriptionStatus || 'active',
            subscriptionStartDate: data.subscriptionStartDate?.toDate(),
            subscriptionEndDate: data.subscriptionEndDate?.toDate(),

            // Activity info
            lastLoginAt: data.lastLoginAt?.toDate(),
            lastActiveAt: data.lastActiveAt?.toDate(),
            loginCount: data.loginCount || 0,
            
            // Learning progress (would be calculated from userProgress collection)
            coursesEnrolled: 0,
            coursesCompleted: 0,
            totalLearningTime: 0,
            skillsAssessmentCompleted: data.skillsAssessmentCompleted,
            skillsAssessmentCompletedAt: data.skillsAssessmentCompletedAt?.toDate(),
            
            // Community activity (would be calculated from posts/replies)
            postsCount: 0,
            repliesCount: 0,
            likesReceived: 0,
            reputation: 0,
            
            // Moderation info
            warningsCount: data.warningsCount || 0,
            suspensionsCount: data.suspensionsCount || 0,
            lastModerationAction: data.lastModerationAction?.toDate(),
            moderationNotes: data.moderationNotes,
            
            // Geographic info
            country: data.country,
            city: data.city,
            timezone: data.timezone,
            preferredLanguage: data.preferredLanguage || 'en',
            
            // Timestamps
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          });
        }
      });

      return {
        users,
        hasMore: snapshot.docs.length > pageLimit,
        lastDoc: snapshot.docs[pageLimit - 1] || null,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user status
   */
  async updateUserStatus(userId: string, status: UserStatus, reason?: string): Promise<void> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        status,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      // Log the action
      await this.logAuditAction('user_status_update', 'user', userId, {
        newStatus: status,
        reason,
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reset skills assessment cooldown for a user
   */
  async resetAssessmentCooldown(userId: string): Promise<void> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        lastAssessmentDate: null,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      // Log the action
      await this.logAuditAction('assessment_cooldown_reset', 'user', userId, {
        action: 'reset_assessment_cooldown',
      });
    } catch (error) {
      console.error('Error resetting assessment cooldown:', error);
      throw error;
    }
  }

  /**
   * Create moderation action
   */
  async createModerationAction(actionData: Omit<ModerationAction, 'id' | 'createdAt' | 'updatedAt' | 'moderatorId' | 'moderatorName'>): Promise<string> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      // Get moderator info
      const moderatorDoc = await getDoc(doc(db, 'users', user.uid));
      const moderatorData = moderatorDoc.data();
      
      const action = {
        ...actionData,
        moderatorId: user.uid,
        moderatorName: moderatorData?.displayName || user.email || 'Unknown',
        status: 'active' as const,
        appealSubmitted: false,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, 'moderationActions'), action);

      // Update user's moderation count if targeting a user
      if (actionData.targetUserId) {
        const userRef = doc(db, 'users', actionData.targetUserId);
        const updateData: any = {
          lastModerationAction: Timestamp.fromDate(new Date()),
          updatedAt: Timestamp.fromDate(new Date()),
        };

        if (actionData.type === 'warning') {
          updateData.warningsCount = increment(1);
        } else if (actionData.type === 'suspension' || actionData.type === 'ban') {
          updateData.suspensionsCount = increment(1);
          updateData.status = actionData.type === 'ban' ? UserStatus.BANNED : UserStatus.SUSPENDED;
        }

        await updateDoc(userRef, updateData);
      }

      // Log the action
      await this.logAuditAction('moderation_action_created', 'moderation_action', docRef.id, {
        type: actionData.type,
        targetUserId: actionData.targetUserId,
        reason: actionData.reason,
      });

      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  // ==================== CONTENT MANAGEMENT ====================

  /**
   * Get content items with filtering
   */
  async getContentItems(filters?: {
    type?: string;
    status?: ContentStatus;
    authorId?: string;
    limit?: number;
  }): Promise<any[]> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      // This would query multiple collections based on type
      // For now, returning courses as an example
      let q = query(
        collection(db, 'courses'),
        orderBy('updatedAt', 'desc')
      );

      if (filters?.limit) {
        q = query(q, limit(filters.limit));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        type: 'course',
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      }));
    } catch (error) {
      throw error;
    }
  }

  // ==================== SYSTEM SETTINGS ====================

  /**
   * Get system settings
   */
  async getSystemSettings(category?: string): Promise<SystemSettings[]> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      let q = query(
        collection(db, 'systemSettings'),
        orderBy('category'),
        orderBy('key')
      );

      if (category) {
        q = query(q, where('category', '==', category));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastModifiedAt: doc.data().lastModifiedAt?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as SystemSettings[];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update system setting
   */
  async updateSystemSetting(settingId: string, value: any, reason?: string): Promise<void> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      const settingRef = doc(db, 'systemSettings', settingId);
      const settingDoc = await getDoc(settingRef);
      
      if (!settingDoc.exists()) {
        throw new Error('Setting not found');
      }

      const oldValue = settingDoc.data().value;
      
      await updateDoc(settingRef, {
        value,
        lastModifiedBy: user.uid,
        lastModifiedAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
        changeHistory: arrayUnion({
          id: `change_${Date.now()}`,
          oldValue,
          newValue: value,
          changedBy: user.uid,
          changedAt: Timestamp.fromDate(new Date()),
          reason,
        }),
      });

      // Log the action
      await this.logAuditAction('system_setting_updated', 'system_setting', settingId, {
        oldValue,
        newValue: value,
        reason,
      });
    } catch (error) {
      throw error;
    }
  }

  // ==================== AUDIT LOGGING ====================

  /**
   * Log audit action
   */
  private async logAuditAction(
    action: string,
    resource: string,
    resourceId?: string,
    context?: Record<string, any>
  ): Promise<void> {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const auditLog: Omit<AuditLog, 'id'> = {
        action,
        resource,
        resourceId,
        userId: user.uid,
        userEmail: user.email || undefined,
        userRole: 'admin', // Would get from user data
        ipAddress: '0.0.0.0', // Would get from request
        userAgent: navigator.userAgent,
        context,
        success: true,
        createdAt: new Date(),
      };

      await addDoc(collection(db, 'auditLogs'), {
        ...auditLog,
        createdAt: Timestamp.fromDate(auditLog.createdAt),
      });
    } catch (error) {
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Get date range for analytics period
   */
  private getDateRange(period: AnalyticsTimePeriod): { startDate: Date; endDate: Date } {
    const endDate = new Date();
    const startDate = new Date();

    switch (period) {
      case AnalyticsTimePeriod.TODAY:
        startDate.setHours(0, 0, 0, 0);
        break;
      case AnalyticsTimePeriod.YESTERDAY:
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(endDate.getDate() - 1);
        endDate.setHours(23, 59, 59, 999);
        break;
      case AnalyticsTimePeriod.LAST_7_DAYS:
        startDate.setDate(startDate.getDate() - 7);
        break;
      case AnalyticsTimePeriod.LAST_30_DAYS:
        startDate.setDate(startDate.getDate() - 30);
        break;
      case AnalyticsTimePeriod.LAST_90_DAYS:
        startDate.setDate(startDate.getDate() - 90);
        break;
      case AnalyticsTimePeriod.LAST_YEAR:
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
      default:
        startDate.setDate(startDate.getDate() - 30);
    }

    return { startDate, endDate };
  }

  /**
   * Calculate growth rate
   */
  private calculateGrowthRate(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  }

  /**
   * Calculate engagement score
   */
  private calculateEngagementScore(posts: number, replies: number, completedCourses: number): number {
    return Math.min(100, (posts * 2) + replies + (completedCourses * 5));
  }

  // ==================== UTILITIES: SEED SAMPLE DATA ====================
  /**
   * Seed Firestore with sample data for local development and demos
   * Creates sample users, courses, enrollments (userProgress), posts, and messages.
   */
  async seedSampleData(): Promise<{ users: number; courses: number; enrollments: number; posts: number; messages: number }> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    // Helper to create a recent date within last N days
    const daysAgo = (n: number) => {
      const d = new Date();
      d.setDate(d.getDate() - Math.floor(Math.random() * n));
      return d;
    };

    // Create users
    const usersToCreate = 20;
    const createdUserIds: string[] = [];
    for (let i = 1; i <= usersToCreate; i++) {
      const userDoc = await addDoc(collection(db, 'users'), {
        email: `user${i}@example.com`,
        displayName: `User ${i}`,
        roles: ['student'],
        isActive: true,
        createdAt: Timestamp.fromDate(daysAgo(45)),
        updatedAt: Timestamp.fromDate(new Date()),
        lastActiveAt: Timestamp.fromDate(daysAgo(5)),
        profileComplete: Math.random() > 0.3,
      });
      createdUserIds.push(userDoc.id);
    }

    // Create courses
    const coursesToCreate = 10;
    const createdCourseIds: string[] = [];
    for (let i = 1; i <= coursesToCreate; i++) {
      const courseDoc = await addDoc(collection(db, 'courses'), {
        title: `Sample Course ${i}`,
        description: 'This is a sample course for demo analytics.',
        content: 'Markdown content here... ',
        competency: 'promptEngineering',
        level: (i % 5) + 1,
        estimatedDuration: 60 + i * 5,
        tags: ['sample', 'demo'],
        createdBy: 'seed-script',
        createdAt: Timestamp.fromDate(daysAgo(60)),
        updatedAt: Timestamp.fromDate(new Date()),
        isPublished: i % 2 === 0 || Math.random() > 0.3,
      });
      createdCourseIds.push(courseDoc.id);
    }

    // Create enrollments (userProgress)
    const enrollmentsToCreate = 40;
    for (let i = 0; i < enrollmentsToCreate; i++) {
      const userId = createdUserIds[i % createdUserIds.length];
      const courseId = createdCourseIds[i % createdCourseIds.length];
      const completed = Math.random() > 0.5;
      await addDoc(collection(db, 'user_progress'), {
        userId,
        courseId,
        completedLessons: [],
        completionPercentage: completed ? 100 : Math.floor(Math.random() * 80),
        startedAt: Timestamp.fromDate(daysAgo(30)),
        completedAt: completed ? Timestamp.fromDate(daysAgo(10)) : null,
        lastAccessedAt: Timestamp.fromDate(daysAgo(7)),
        status: completed ? 'completed' : 'in_progress',
        totalTimeSpent: Math.floor(Math.random() * 7200),
      });
    }

    // Create posts
    const postsToCreate = 30;
    for (let i = 1; i <= postsToCreate; i++) {
      await addDoc(collection(db, 'posts'), {
        authorId: createdUserIds[i % createdUserIds.length],
        title: `Sample Post ${i}`,
        content: 'This is a sample community post for analytics.',
        createdAt: Timestamp.fromDate(daysAgo(20)),
        updatedAt: Timestamp.fromDate(new Date()),
        likes: Math.floor(Math.random() * 20),
        comments: Math.floor(Math.random() * 10),
      });
    }

    // Create messages
    const messagesToCreate = 60;
    for (let i = 1; i <= messagesToCreate; i++) {
      await addDoc(collection(db, 'messages'), {
        conversationId: `conv_${Math.ceil(i / 5)}`,
        senderId: createdUserIds[i % createdUserIds.length],
        senderDisplayName: `User ${i % createdUserIds.length}`,
        content: `Sample message ${i}`,
        timestamp: Timestamp.fromDate(daysAgo(15)),
        readBy: [],
        messageType: 'text',
      });
    }

    return {
      users: usersToCreate,
      courses: coursesToCreate,
      enrollments: enrollmentsToCreate,
      posts: postsToCreate,
      messages: messagesToCreate,
    };
  }

  /**
   * Update user subscription
   */
  async updateUserSubscription(userId: string, tier: string, endDate?: Date): Promise<void> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      const userRef = doc(db, 'users', userId);
      const updateData: any = {
        subscriptionTier: tier,
        subscriptionStatus: 'active',
        updatedAt: Timestamp.fromDate(new Date()),
      };

      if (endDate) {
        updateData.subscriptionEndDate = Timestamp.fromDate(endDate);
      } else {
        updateData.subscriptionEndDate = null;
      }

      await updateDoc(userRef, updateData);

      // Log the action
      await this.logAuditAction('user_subscription_update', 'user', userId, {
        newTier: tier,
        endDate,
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update user roles
   */
  async updateUserRoles(userId: string, roles: string[]): Promise<void> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        roles,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      // Log the action
      await this.logAuditAction('user_role_update', 'user', userId, {
        newRoles: roles,
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete user (Firestore only - Auth deletion requires Cloud Functions)
   */
  async deleteUser(userId: string): Promise<void> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      // Soft delete by marking status as deleted or actually delete the doc?
      // For now, let's just update status to 'deleted' to preserve data integrity
      // or actually delete if requested. Let's do a soft delete/ban for safety
      // But user asked to "delete", so let's actually delete the user doc.
      // However, deleting the doc might break references.
      // Let's stick to "Banned" or "Deleted" status for safety in this frontend-only context.
      // But wait, the prompt says "delete users".
      // I'll implement a method that updates status to 'deleted' (if that status exists) or just deletes the doc.
      // Let's check UserStatus enum.
      
      // Assuming we want to remove the user from the list.
      // I will use deleteDoc from firestore.
      const { deleteDoc } = await import('firebase/firestore'); 
      await deleteDoc(doc(db, 'users', userId));

      // Log the action
      await this.logAuditAction('user_deleted', 'user', userId, {});
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get full user details including learning history
   */
  async getUserDetails(userId: string): Promise<any> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      const userData = userDoc.data();

      // Get learning history
      const progressQuery = query(
        collection(db, 'user_progress'),
        where('userId', '==', userId)
      );
      const progressSnapshot = await getDocs(progressQuery);
      
      // Fetch course details for each progress record
      const learningHistory = await Promise.all(progressSnapshot.docs.map(async (progressDoc) => {
        const data = progressDoc.data();
        let courseTitle = 'Unknown Course';
        
        try {
          if (data.courseId) {
            const courseDoc = await getDoc(doc(db, 'courses', data.courseId));
            if (courseDoc.exists()) {
              courseTitle = courseDoc.data().title;
            }
          }
        } catch (e) {
          console.error('Error fetching course details', e);
        }

        return {
          id: progressDoc.id,
          ...data,
          courseTitle,
          startedAt: data.startedAt?.toDate(),
          completedAt: data.completedAt?.toDate(),
          lastAccessedAt: data.lastAccessedAt?.toDate(),
        };
      }));

      // Get billing history (mock for now as it would be in a subcollection or Stripe)
      // In a real app, we might query a 'subscriptions' or 'invoices' collection
      const billingHistory: any[] = [];

      return {
        user: {
            id: userDoc.id,
            ...userData,
            createdAt: userData.createdAt?.toDate(),
            lastActiveAt: userData.lastActiveAt?.toDate(),
            subscriptionEndDate: userData.subscriptionEndDate?.toDate(),
        },
        learningHistory,
        billingHistory
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get email history with filtering and pagination
   */
  public async getEmailHistory(options?: {
    status?: string;
    limit?: number;
    startAfter?: string;
    recipientEmail?: string;
    templateId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{ emails: any[]; hasMore: boolean }> {
    try {
      const getEmailHistoryFn = httpsCallable(functions, 'getEmailHistory');
      const result = await getEmailHistoryFn(options || {});
      const data = result.data as any;
      
      return {
        emails: data.emails || [],
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error fetching email history:', error);
      throw error;
    }
  }

  /**
   * Get detailed information about a specific email
   */
  public async getEmailDetails(emailId: string): Promise<any> {
    try {
      const getEmailDetailsFn = httpsCallable(functions, 'getEmailDetails');
      const result = await getEmailDetailsFn({ emailId });
      const data = result.data as any;
      
      return data.email;
    } catch (error) {
      console.error('Error fetching email details:', error);
      throw error;
    }
  }

  /**
   * Send manual email to selected recipients
   */
  public async sendManualEmail(emailData: {
    recipients?: string[];
    subject: string;
    htmlBody: string;
    textBody?: string;
    sendToAll?: boolean;
    audienceType?: string;
    scheduleAt?: string | null;
  }): Promise<{ message: string; emailCount: number }> {
    try {
      const sendManualEmailFn = httpsCallable(functions, 'sendManualEmail');
      const result = await sendManualEmailFn(emailData);
      const data = result.data as any;
      
      return {
        message: data.message,
        emailCount: data.emailCount,
      };
    } catch (error) {
      console.error('Error sending manual email:', error);
      throw error;
    }
  }

  /**
   * Get list of users for recipient selection
   */
  public async getEmailRecipients(options?: {
    searchQuery?: string;
    limit?: number;
    startAfter?: string;
    subscriptionTier?: string;
  }): Promise<{ users: any[]; hasMore: boolean }> {
    try {
      const getEmailRecipientsFn = httpsCallable(functions, 'getEmailRecipients');
      const result = await getEmailRecipientsFn(options || {});
      const data = result.data as any;
      
      return {
        users: data.users || [],
        hasMore: data.hasMore || false,
      };
    } catch (error) {
      console.error('Error fetching email recipients:', error);
      throw error;
    }
  }

  /**
   * Get email statistics
   */
  public async getEmailStats(period: string = 'last_30_days'): Promise<any> {
    try {
      const getEmailStatsFn = httpsCallable(functions, 'getEmailStats');
      const result = await getEmailStatsFn({ period });
      const data = result.data as any;
      
      return data.stats;
    } catch (error) {
      console.error('Error fetching email stats:', error);
      throw error;
    }
  }

  /**
   * Resend an existing email
   */
  public async resendEmail(emailId: string): Promise<{ message: string; newEmailId: string }> {
    try {
      const resendEmailFn = httpsCallable(functions, 'resendEmail');
      const result = await resendEmailFn({ emailId });
      const data = result.data as any;

      return {
        message: data.message,
        newEmailId: data.newEmailId,
      };
    } catch (error) {
      console.error('Error resending email:', error);
      throw error;
    }
  }

  // ==================== SUPPORT TICKETS ====================

  /**
   * Get support tickets with filtering and pagination
   */
  async getSupportTickets(filters?: {
    status?: 'open' | 'in_progress' | 'waiting_for_user' | 'resolved' | 'closed';
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    category?: string;
    assignedTo?: string;
    userId?: string;
    limit?: number;
    lastDoc?: any;
  }): Promise<{ tickets: any[]; hasMore: boolean; lastDoc: any }> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      let q = query(
        collection(db, 'support_tickets'),
        orderBy('createdAt', 'desc')
      );

      // Apply filters
      if (filters?.status) {
        q = query(q, where('status', '==', filters.status));
      }
      if (filters?.priority) {
        q = query(q, where('priority', '==', filters.priority));
      }
      if (filters?.assignedTo) {
        q = query(q, where('assignedTo', '==', filters.assignedTo));
      }
      if (filters?.userId) {
        q = query(q, where('userId', '==', filters.userId));
      }

      const pageLimit = filters?.limit || 50;
      q = query(q, limit(pageLimit + 1));

      if (filters?.lastDoc) {
        q = query(q, startAfter(filters.lastDoc));
      }

      const snapshot = await getDocs(q);
      const tickets = snapshot.docs.slice(0, pageLimit).map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        resolvedAt: doc.data().resolvedAt?.toDate(),
        firstResponseAt: doc.data().firstResponseAt?.toDate(),
      }));

      return {
        tickets,
        hasMore: snapshot.docs.length > pageLimit,
        lastDoc: snapshot.docs[pageLimit - 1] || null,
      };
    } catch (error) {
      console.error('Error fetching support tickets:', error);
      throw error;
    }
  }

  /**
   * Get a single support ticket by ID
   */
  async getSupportTicket(ticketId: string): Promise<any> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      const ticketDoc = await getDoc(doc(db, 'support_tickets', ticketId));
      if (!ticketDoc.exists()) {
        throw new Error('Ticket not found');
      }

      const ticketData = ticketDoc.data();

      // Get messages subcollection
      const messagesQuery = query(
        collection(db, 'support_tickets', ticketId, 'messages'),
        orderBy('createdAt', 'asc')
      );
      const messagesSnapshot = await getDocs(messagesQuery);
      const messages = messagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
      }));

      return {
        id: ticketDoc.id,
        ...ticketData,
        messages,
        createdAt: ticketData.createdAt?.toDate(),
        updatedAt: ticketData.updatedAt?.toDate(),
        resolvedAt: ticketData.resolvedAt?.toDate(),
        firstResponseAt: ticketData.firstResponseAt?.toDate(),
      };
    } catch (error) {
      console.error('Error fetching support ticket:', error);
      throw error;
    }
  }

  /**
   * Create a new support ticket
   */
  async createSupportTicket(ticketData: {
    subject: string;
    description: string;
    category: 'technical' | 'billing' | 'account' | 'course' | 'community' | 'other';
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    userId?: string;
    userEmail: string;
    userName: string;
  }): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      // Generate ticket number
      const ticketNumber = `TKT-${Date.now().toString(36).toUpperCase()}`;

      const ticket = {
        ticketNumber,
        subject: ticketData.subject,
        description: ticketData.description,
        category: ticketData.category,
        priority: ticketData.priority || 'medium',
        status: 'open' as const,
        userId: ticketData.userId || user.uid,
        userEmail: ticketData.userEmail,
        userName: ticketData.userName,
        attachments: [],
        tags: [],
        labels: [],
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const docRef = await addDoc(collection(db, 'support_tickets'), ticket);

      // Add initial message with the description
      await addDoc(collection(db, 'support_tickets', docRef.id, 'messages'), {
        ticketId: docRef.id,
        senderId: user.uid,
        senderName: ticketData.userName,
        senderType: 'user',
        content: ticketData.description,
        isInternal: false,
        attachments: [],
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      // Log the action
      await this.logAuditAction('support_ticket_created', 'support_ticket', docRef.id, {
        ticketNumber,
        category: ticketData.category,
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating support ticket:', error);
      throw error;
    }
  }

  /**
   * Update a support ticket
   */
  async updateSupportTicket(ticketId: string, updates: {
    status?: 'open' | 'in_progress' | 'waiting_for_user' | 'resolved' | 'closed';
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    assignedTo?: string;
    resolution?: string;
    tags?: string[];
    labels?: string[];
  }): Promise<void> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      const ticketRef = doc(db, 'support_tickets', ticketId);
      const ticketDoc = await getDoc(ticketRef);

      if (!ticketDoc.exists()) {
        throw new Error('Ticket not found');
      }

      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date()),
      };

      // Track resolution
      if (updates.status === 'resolved' && ticketDoc.data().status !== 'resolved') {
        updateData.resolvedAt = Timestamp.fromDate(new Date());
        updateData.resolvedBy = user.uid;

        // Calculate resolution time
        const createdAt = ticketDoc.data().createdAt?.toDate();
        if (createdAt) {
          updateData.resolutionTime = Math.round((Date.now() - createdAt.getTime()) / 60000); // in minutes
        }
      }

      // Track assignment
      if (updates.assignedTo && !ticketDoc.data().assignedTo) {
        updateData.assignedAt = Timestamp.fromDate(new Date());
      }

      await updateDoc(ticketRef, updateData);

      // Log the action
      await this.logAuditAction('support_ticket_updated', 'support_ticket', ticketId, updates);
    } catch (error) {
      console.error('Error updating support ticket:', error);
      throw error;
    }
  }

  /**
   * Add a message to a support ticket
   */
  async addTicketMessage(ticketId: string, messageData: {
    content: string;
    isInternal?: boolean;
    attachments?: any[];
  }): Promise<string> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      // Get user info
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();

      const message = {
        ticketId,
        senderId: user.uid,
        senderName: userData?.displayName || user.email || 'Support Agent',
        senderType: 'agent' as const,
        content: messageData.content,
        isInternal: messageData.isInternal || false,
        attachments: messageData.attachments || [],
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      const messageRef = await addDoc(
        collection(db, 'support_tickets', ticketId, 'messages'),
        message
      );

      // Update ticket's updatedAt and track first response time
      const ticketRef = doc(db, 'support_tickets', ticketId);
      const ticketDoc = await getDoc(ticketRef);
      const ticketData = ticketDoc.data();

      const ticketUpdate: any = {
        updatedAt: Timestamp.fromDate(new Date()),
      };

      // Track first response time (only for non-internal agent messages)
      if (!messageData.isInternal && !ticketData?.firstResponseAt) {
        ticketUpdate.firstResponseAt = Timestamp.fromDate(new Date());
        const createdAt = ticketData?.createdAt?.toDate();
        if (createdAt) {
          ticketUpdate.firstResponseTime = Math.round((Date.now() - createdAt.getTime()) / 60000); // in minutes
        }
      }

      // Update status to in_progress if it was open
      if (ticketData?.status === 'open' && !messageData.isInternal) {
        ticketUpdate.status = 'in_progress';
      }

      await updateDoc(ticketRef, ticketUpdate);

      return messageRef.id;
    } catch (error) {
      console.error('Error adding ticket message:', error);
      throw error;
    }
  }

  /**
   * Get support ticket analytics for dashboard
   */
  async getSupportTicketAnalytics(): Promise<{
    totalTickets: number;
    openTickets: number;
    averageResponseTime: number;
    averageResolutionTime: number;
    customerSatisfactionScore: number;
    ticketsByStatus: Record<string, number>;
    ticketsByPriority: Record<string, number>;
    ticketsByCategory: Record<string, number>;
  }> {
    if (!(await this.checkAdminAccess())) {
      throw new Error('Insufficient permissions');
    }

    try {
      // Get all tickets
      const ticketsSnapshot = await getDocs(collection(db, 'support_tickets'));
      const totalTickets = ticketsSnapshot.size;

      // Initialize counters
      let openTickets = 0;
      let totalResponseTime = 0;
      let responseTimeCount = 0;
      let totalResolutionTime = 0;
      let resolutionTimeCount = 0;
      let totalSatisfaction = 0;
      let satisfactionCount = 0;

      const ticketsByStatus: Record<string, number> = {
        open: 0,
        in_progress: 0,
        waiting_for_user: 0,
        resolved: 0,
        closed: 0,
      };

      const ticketsByPriority: Record<string, number> = {
        low: 0,
        medium: 0,
        high: 0,
        urgent: 0,
      };

      const ticketsByCategory: Record<string, number> = {
        technical: 0,
        billing: 0,
        account: 0,
        course: 0,
        community: 0,
        other: 0,
      };

      ticketsSnapshot.docs.forEach(doc => {
        const data = doc.data();

        // Count by status
        const status = data.status || 'open';
        ticketsByStatus[status] = (ticketsByStatus[status] || 0) + 1;

        // Count open tickets
        if (status === 'open' || status === 'in_progress' || status === 'waiting_for_user') {
          openTickets++;
        }

        // Count by priority
        const priority = data.priority || 'medium';
        ticketsByPriority[priority] = (ticketsByPriority[priority] || 0) + 1;

        // Count by category
        const category = data.category || 'other';
        ticketsByCategory[category] = (ticketsByCategory[category] || 0) + 1;

        // Sum response times
        if (data.firstResponseTime) {
          totalResponseTime += data.firstResponseTime;
          responseTimeCount++;
        }

        // Sum resolution times
        if (data.resolutionTime) {
          totalResolutionTime += data.resolutionTime;
          resolutionTimeCount++;
        }

        // Sum satisfaction ratings
        if (data.satisfactionRating) {
          totalSatisfaction += data.satisfactionRating;
          satisfactionCount++;
        }
      });

      // Calculate averages
      const averageResponseTime = responseTimeCount > 0
        ? Math.round((totalResponseTime / responseTimeCount) / 60 * 10) / 10 // Convert to hours
        : 0;

      const averageResolutionTime = resolutionTimeCount > 0
        ? Math.round((totalResolutionTime / resolutionTimeCount) / 60 * 10) / 10 // Convert to hours
        : 0;

      const customerSatisfactionScore = satisfactionCount > 0
        ? Math.round((totalSatisfaction / satisfactionCount) * 10) / 10
        : 0;

      return {
        totalTickets,
        openTickets,
        averageResponseTime,
        averageResolutionTime,
        customerSatisfactionScore,
        ticketsByStatus,
        ticketsByPriority,
        ticketsByCategory,
      };
    } catch (error) {
      console.error('Error fetching support ticket analytics:', error);
      // Return zeros if we can't calculate
      return {
        totalTickets: 0,
        openTickets: 0,
        averageResponseTime: 0,
        averageResolutionTime: 0,
        customerSatisfactionScore: 0,
        ticketsByStatus: {},
        ticketsByPriority: {},
        ticketsByCategory: {},
      };
    }
  }

  /**
   * Submit satisfaction rating for a resolved ticket
   */
  async submitTicketSatisfaction(ticketId: string, rating: number, feedback?: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    try {
      const ticketRef = doc(db, 'support_tickets', ticketId);
      const ticketDoc = await getDoc(ticketRef);

      if (!ticketDoc.exists()) {
        throw new Error('Ticket not found');
      }

      // Verify user owns the ticket
      if (ticketDoc.data().userId !== user.uid) {
        throw new Error('Unauthorized');
      }

      await updateDoc(ticketRef, {
        satisfactionRating: rating,
        satisfactionFeedback: feedback || null,
        updatedAt: Timestamp.fromDate(new Date()),
      });
    } catch (error) {
      console.error('Error submitting ticket satisfaction:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const adminService = AdminService.getInstance();

export default adminService;
